// Khung hội thoại + ô soạn tin dùng chung cho cả hai phía (khách ở portal,
// admin ở /customer-chats). Phía nào là "mình" quyết định bubble nào nằm phải.
import { useEffect, useMemo, useRef, useState } from "react";
import { ImageLightbox } from "@/components/ImageLightbox";
import { PhotoThumb } from "@/components/PhotoThumb";
import type { ChatMessage } from "./api";
import { ChatAvatar } from "./ChatAvatar";
import { formatChatTime, groupChatMessages, seenLabel } from "./chat-format";

/** Tên hiện trên bong bóng. Không tra được hồ sơ → "Gạo Nâu", KHÔNG hiện mã. */
function tenNguoiGui(message: ChatMessage): string {
  if (message.senderType !== "admin") return "Khách";
  return message.sender?.name ?? "Gạo Nâu";
}

export function ChatThread({
  messages,
  viewer,
  attachmentUrl,
  emptyText,
  // Ba prop dưới CHỈ màn nhân viên truyền. Cổng khách để mặc định → không hiện
  // "khách đã xem", không hiện nút trả lời. Đây là chỗ ép luật một chiều của
  // quyết định 0063 mục 4 ở tầng giao diện; tầng máy chủ đã ép ở DTO.
  customerLastReadAt,
  onReply,
  highlightText,
}: {
  messages: ChatMessage[];
  viewer: "customer" | "admin";
  attachmentUrl: (message: ChatMessage) => string | null;
  emptyText: string;
  customerLastReadAt?: string | null | undefined;
  onReply?: ((message: ChatMessage) => void) | undefined;
  highlightText?: string | undefined;
}) {
  const endRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length]);

  const rows = useMemo(() => groupChatMessages(messages), [messages]);
  // Nhãn "đã xem" chỉ gắn dưới tin CUỐI CÙNG của mình — gắn mọi tin là một cột
  // chữ lặp lại, đúng cách WhatsApp làm.
  const lastMineId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      if (messages[i]!.senderType === viewer) return messages[i]!.id;
    }
    return null;
  }, [messages, viewer]);

  // Hộp phóng to: gom MỌI ảnh của cuộc chat theo thứ tự thời gian để ← →
  // chuyển qua lại được, thay vì mỗi ảnh một hộp riêng. attachmentUrl là hàm
  // inline từ nơi gọi nên danh sách tính lại mỗi lần vẽ — trần 200 tin, rẻ.
  const [lightbox, setLightbox] = useState(-1);
  const imageUrls = useMemo(
    () => messages.map(attachmentUrl).filter((url): url is string => Boolean(url)),
    [messages, attachmentUrl],
  );

  return (
    <div className="flex-1 space-y-1 overflow-y-auto p-3">
      {rows.length === 0 ? (
        <p className="px-2 py-6 text-center text-sm text-muted-foreground">{emptyText}</p>
      ) : (
        rows.map((row) => {
          if (row.kind === "day") {
            return (
              <p key={row.key} className="my-3 text-center">
                <span className="rounded-full bg-surface-2 px-3 py-1 text-[11px] text-muted-foreground">
                  {row.label}
                </span>
              </p>
            );
          }
          const { message, showHeader, showTail } = row;
          const mine = message.senderType === viewer;
          const imgSrc = attachmentUrl(message);
          const senderName = tenNguoiGui(message);

          return (
            <div
              key={row.key}
              className={
                mine ? "bubble-in flex justify-end gap-2" : "bubble-in flex justify-start gap-2"
              }
            >
              {!mine &&
                (showHeader ? (
                  <ChatAvatar name={senderName} src={message.sender?.avatarUrl ?? null} />
                ) : (
                  // Ô giữ chỗ để bong bóng trong cùng cụm thẳng hàng nhau.
                  <span aria-hidden="true" className="w-8 shrink-0" />
                ))}
              <div className="max-w-[80%]">
                {!mine && showHeader && message.senderType === "admin" && (
                  <p className="mb-0.5 px-1 text-[11px] text-muted-foreground">
                    <span className="font-medium text-foreground">{senderName}</span>
                    {message.sender?.title ? ` · ${message.sender.title}` : ""}
                  </p>
                )}
                <div
                  className={
                    mine
                      ? "rounded-2xl rounded-br-md bg-[var(--color-brand)] px-3 py-2 text-white"
                      : "rounded-2xl rounded-bl-md border bg-surface-2 px-3 py-2"
                  }
                >
                  {message.replyTo && (
                    <div
                      className={
                        mine
                          ? "mb-1 rounded-lg border-l-2 border-white/60 bg-white/15 px-2 py-1 text-[11px]"
                          : "mb-1 rounded-lg border-l-2 border-[var(--color-brand)] bg-surface px-2 py-1 text-[11px]"
                      }
                    >
                      <span className="block font-medium">
                        {message.replyTo.senderType === "customer"
                          ? "Khách"
                          : message.replyTo.senderName || "Gạo Nâu"}
                      </span>
                      <span className="block truncate opacity-80">{message.replyTo.preview}</span>
                    </div>
                  )}
                  {imgSrc && (
                    // Nút mở hộp phóng to ngay trong trang (CEO 14/09/2026) thay
                    // cho <a target="_blank">: mở tab mới là mất chỗ đang chat.
                    // Khung ảnh chuẩn 8x10 / 10x8 (CEO chốt 27/09/2026): bill
                    // chuyển khoản khách gửi là ảnh dài — ô cao 208px cũ chỉ
                    // rộng ~96px, không đọc được số tiền.
                    <PhotoThumb
                      src={imgSrc}
                      alt="Ảnh trong chat"
                      onOpen={() => setLightbox(imageUrls.indexOf(imgSrc))}
                      className="mb-1 rounded-xl"
                    />
                  )}
                  {message.body && (
                    <p className="whitespace-pre-wrap break-words text-sm">
                      {highlightText ? highlightMatches(message.body, highlightText) : message.body}
                    </p>
                  )}
                  {message.topic === "affiliate" && (
                    <span className="mt-1 inline-block rounded bg-amber-100 px-1 text-[10px] text-amber-900">
                      Giới thiệu
                    </span>
                  )}
                  {showTail && (
                    <p
                      className={
                        mine
                          ? "mt-1 text-right text-[10px] text-white/95"
                          : "mt-1 text-right text-[10px] text-muted-foreground"
                      }
                    >
                      {formatChatTime(message.createdAt)}
                      {mine && customerLastReadAt !== undefined && message.id === lastMineId && (
                        <span className="ml-1">· {seenLabel(message.createdAt, customerLastReadAt)}</span>
                      )}
                    </p>
                  )}
                </div>
                {onReply && (
                  <button
                    type="button"
                    onClick={() => onReply(message)}
                    className={`mt-0.5 px-1 text-[11px] text-muted-foreground underline-offset-2 hover:underline ${
                      mine ? "float-right" : ""
                    }`}
                  >
                    Trả lời
                  </button>
                )}
              </div>
            </div>
          );
        })
      )}
      <ImageLightbox
        urls={imageUrls}
        index={lightbox}
        onIndex={setLightbox}
        onClose={() => setLightbox(-1)}
        origHref={(i) => imageUrls[i] ?? ""}
      />
      <div ref={endRef} />
    </div>
  );
}

/**
 * Tô chữ khớp ô tìm kiếm.
 *
 * Escape TRƯỚC khi dựng RegExp: người dùng gõ "(" vào ô tìm mà không escape là
 * RegExp ném lỗi và trắng cả khung chat.
 */
export function highlightMatches(text: string, needle: string) {
  const q = needle.trim();
  if (!q) return text;
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === q.toLowerCase() ? (
      <mark key={i} className="rounded bg-amber-200 px-0.5 text-inherit">
        {part}
      </mark>
    ) : (
      <span key={i}>{part}</span>
    ),
  );
}
